import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, CheckIcon, GlobeIcon, Loader2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { SecuritySection } from '../../components/admin/SecuritySection';
import { NotificationsSection } from '../../components/admin/NotificationsSection';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL } from '../../data/site';
export function Settings() {
  const {
    settings,
    settingsLoading,
    saveSettings
  } = useAdminData();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setDraft(settings);
  }, [settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  function field(key: keyof typeof draft) {
    return {
      value: draft[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft({
          ...draft,
          [key]: event.target.value
        });
        setSaved(false);
        setError('');
      }
    };
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1777" data-mp="2eu2296oa9hvq">
      <p className="text-sm text-ink-muted" data-id="element-1778" data-mp="2ss9xna5o8b4s">Firmaoplysninger, kontakt og standardpriser</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1779" data-mp="2nrcg8zshjl4c">
        Indstillinger
      </h1>
      <p className="mt-3 max-w-2xl text-[15px] text-ink-soft" data-id="element-1780" data-mp="31pspoyhqfpq6">
        Oplysningerne herunder er hjemmesidens ene fælles kilde. De bruges automatisk i topbjælken,
        hero-sektionen, kontaktafsnittet og footeren — du retter dem kun ét sted.
      </p>

      <p className="mt-5 flex max-w-2xl items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-1781" data-mp="36k6kayoklsts">
        <GlobeIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-1782" data-mp="3le1mxmy4ulwj" />
        Oplysningerne gemmes globalt og vises for alle besøgende på alle enheder.
      </p>

      <form className="mt-7 max-w-2xl space-y-5" onSubmit={async event => {
      event.preventDefault();
      if (saving) return;
      setSaving(true);
      const result = await saveSettings(draft);
      setSaving(false);
      if (result.ok) {
        setSaved(true);
        setError('');
      } else {
        setSaved(false);
        setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      }
    }} data-id="element-1783" data-mp="3nlu8hqjbus89">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-1784" data-mp="f34zi2vx3ka5">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1785" data-mp="k39f7qp04azb">Virksomhed</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1786" data-mp="p2vr9wt3fl6j">
            <TextField id="in-navn" label="Firmanavn" {...field('companyName')} data-id="element-1787" data-mp="p09jbw2aj3ns" />
            <TextField id="in-cvr" label="CVR-nummer" inputMode="numeric" {...field('cvr')} data-id="element-1788" data-mp="2lwqi6h26snnq" />
            <TextField id="in-tagline" label="Tagline" className="sm:col-span-2" {...field('tagline')} data-id="element-1789" data-mp="2qu8vskr5fr22" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1790" data-mp="2ek12lhyg4c50">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1791" data-mp="2ehoeen23h5ji">Kontaktoplysninger</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1792" data-mp="29ibp7vnr3mdr">
            <TextField id="in-tlf" label="Telefonnummer" type="tel" inputMode="tel" hint="Vises i topbjælken, hero, kontakt og footer." {...field('phone')} data-id="element-1793" data-mp="24mwcj71hbwix" />
            <TextField id="in-mail" label="E-mail" type="email" readOnly disabled value={CONTACT_EMAIL} onChange={() => undefined} hint="Fast adresse — kan ikke ændres." data-id="element-1794" data-mp="2sb5xk6qvcg90" />
            <TextField id="in-adresse" label="Adresse" className="sm:col-span-2" {...field('address')} data-id="element-1795" data-mp="1bww6v5z5mff0" />
            <TextField id="in-web" label="Hjemmeside" className="sm:col-span-2" {...field('website')} data-id="element-1796" data-mp="30rdb0spv5631" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1797" data-mp="35ilk1nna6pfo">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1798" data-mp="2qtfl80scv18b">Standardpriser</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1799" data-mp="2vrh2cg680qh5">
            <TextField id="in-timepris" label="Timepris (kr.)" inputMode="numeric" {...field('hourlyRate')} data-id="element-1800" data-mp="2vpw70oc313i8" />
            <TextField id="in-udkald" label="Udkaldsgebyr (kr.)" inputMode="numeric" {...field('callOutFee')} data-id="element-1801" data-mp="3ogo2tkp8jq51" />
          </div>
          <p className="mt-4 text-sm text-ink-muted" data-id="element-1802" data-mp="39lrkufrmjoer">
            Bruges som forslag, når du opretter en ny sag. Du kan altid ændre prisen på den enkelte sag.
          </p>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1803" data-mp="1y6co9d3buyk6">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1804" data-mp="234wcxl9b2og8">Faktura</h2>
          <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1805" data-mp="l4f1dujbwpql">
            Standardværdier der kopieres ind, når du opretter en ny faktura. De kan altid rettes på
            den enkelte faktura, og ændringer her påvirker ikke fakturaer, du allerede har oprettet.
          </p>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1806" data-mp="q4jfxf0ofae6">
            <TextField id="in-bank" label="Bank" {...field('invoiceBankName')} data-id="element-1807" data-mp="q2ym5rm6rvhd" />
            <TextField id="in-regnr" label="Reg.nr." inputMode="numeric" {...field('invoiceRegistrationNumber')} data-id="element-1808" data-mp="2fgndc4i7x4w" />
            <TextField id="in-kontonr" label="Kontonr." inputMode="numeric" {...field('invoiceAccountNumber')} data-id="element-1809" data-mp="b8bu6a9me0ws" />
            <TextField id="in-betalingsbetingelser" label="Betalingsbetingelser" {...field('invoicePaymentTerms')} data-id="element-1810" data-mp="3evkan5ugc31v" />
            <TextField id="in-betalingstekst" label="Betalingstekst" className="sm:col-span-2" {...field('invoicePaymentText')} data-id="element-1811" data-mp="1dunsv0mci8pr" />
            <TextField id="in-fodnote" label="Standard bemærkning" className="sm:col-span-2" hint="Foreslås som bemærkning til kunden på nye fakturaer." {...field('invoiceFooterNote')} data-id="element-1812" data-mp="1nkh4g1mhmnu0" />
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-4" data-id="element-1813" data-mp="zud01s1h6k1y">
          <Button type="submit" size="lg" disabled={!dirty || saving || settingsLoading} data-id="element-1814" data-mp="zuv61gdxwcn2">
            {saving ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1815" data-mp="19sj3ie2xjy8x" />
                Gemmer …
              </> : 'Gem ændringer'}
          </Button>

          {settingsLoading && <p className="text-[15px] text-ink-muted" data-id="element-1816" data-mp="2l76aqno83mlo">Henter aktuelle oplysninger …</p>}

          {dirty && !saving && !saved && !error && <p className="text-[15px] text-ink-muted" data-id="element-1817" data-mp="9mp1lh27pheq">Der er ændringer, der ikke er gemt.</p>}

          {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1818" data-mp="3vzxczg5kfsv5">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1819" data-mp="3r13xji6pxrsc">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1820" data-mp="3m2iz9ob11i6j" />
              </span>
              Ændringerne er gemt og opdateret på hjemmesiden
            </p>}

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1821" data-mp="3d0jdyjszbrxs">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1822" data-mp="382rn7t42mbv6" />
              {error}
            </p>}
        </div>
      </form>

      <NotificationsSection data-id="element-1823" data-mp="1b7viiwmku8sk" />

      <SecuritySection data-id="element-1824" data-mp="16b4thjsso2v0" />
    </div>;
}